import * as PIXI from 'pixi.js';
import { TypedUpdateFlowMixin } from '../../../updateFlowMixin';
import { TypedParentMixin } from '../../../parentMixin';
import type { LoadSettings } from '../../../serializableMixin';
import { TypedSerializableMixin } from '../../../serializableMixin';
import { BooleanAttribute, NumberAttribute, StringAttribute } from '../../../attributes';
import { DynamicYAlign, DynamicLevel, DynamicXAlign } from '../../../../enums';
import type { DynamicGraphicType } from '../types';
import type { CanvasTextDynamicGraphicRendererSettingsBase } from '../renderer/CanvasTextDynamicGraphicRenderer';
import { cloneDeep } from '../../../../libs/cloneDeep';
import { TextAssetDynamicAnimation } from './TextAssetDynamicAnimation';

/**
 * CanvasTextAssetDynamicGraphic is the Luminary equivalent of CanvasTextDynamicGraphic and provides a bridge between Luminary
 * and the CanvasText library.
 */
export abstract class TextAssetDynamicGraphic extends TypedUpdateFlowMixin(
    TypedParentMixin(TypedSerializableMixin(PIXI.utils.EventEmitter)),
) {
    override _classType = 'CanvasTextDynamicGraphic';

    abstract readonly type: DynamicGraphicType;
    @BooleanAttribute()
    accessor enabled = true;
    // TODO: implement asMask behaviour. This is currently not supported. We only want to use a mask as a mask if this
    //  property is set to true. Otherwise we want to use it as a color graphic. This is so the user can see the shape
    //  of the graphic in the editor. Note, we only want to deal with this on the bridge level. Our canvas API does not
    //  need to be aware of this.
    @BooleanAttribute()
    accessor asMask = false;
    @StringAttribute({ choices: Object.values(DynamicLevel) })
    accessor level = DynamicLevel.object;
    @StringAttribute({ choices: Object.values(DynamicXAlign) })
    accessor alignX = DynamicXAlign.left;
    @StringAttribute({ choices: Object.values(DynamicYAlign) })
    accessor alignY = DynamicYAlign.top;
    @NumberAttribute()
    accessor positionX = 0;
    @NumberAttribute()
    accessor positionY = 0;

    #referenceToOutputMultiplier = 1;

    #animationConfigurations: TextAssetDynamicAnimation[] = [];

    constructor(settings) {
        super(settings);

        this.#referenceToOutputMultiplier = settings.referenceToOutputMultiplier;
    }

    /**
     * The scaling multiplier to take a reference dimension to the output dimension.
     * e.g. myOutputCoord =  myReferenceCoord * this.referenceToOutputMultiplier
     */
    get referenceToOutputMultiplier() {
        return this.#referenceToOutputMultiplier;
    }

    getCanvasRendererSettings(): CanvasTextDynamicGraphicRendererSettingsBase {
        // convert asset animations to canvas animations
        const animationConfigurations = this.#animationConfigurations.map(
            (animationConfiguration) => animationConfiguration.getCanvasRendererSettings(),
        );

        return {
            type: this.type,
            enabled: this.enabled,
            level: this.level,
            alignX: this.alignX,
            alignY: this.alignY,
            positionX: this.positionX * this.referenceToOutputMultiplier,
            positionY: this.positionY * this.referenceToOutputMultiplier,
            animationConfigurations: animationConfigurations,
            asMask: this.asMask,
        };
    }

    get animationConfigurations() {
        return this.#animationConfigurations;
    }

    _animationConfigurationsCustomLoad(properties: Record<string, any>[]) {
        if (!properties) {
            return;
        }
        this.#animationConfigurations.splice(properties.length);
        properties.forEach((data, index) => {
            const animation = this.#animationConfigurations[index] ?? this.addAnimation();
            animation.load(data);
        });
    }

    /**
     * Adds a CanvasTextAssetDynamicAnimation to the `animation` list.
     * The added configurator can be configured to animate any attribute on the asset.
     */
    addAnimation() {
        const animation = new TextAssetDynamicAnimation({
            parent: this,
            updateParentOnUpdate: true,
        });

        // do not mutate, we need to create a new reference for React
        this.#animationConfigurations = [...this.#animationConfigurations, animation];
        this.requiresUpdate = true;

        return animation;
    }

    removeAnimation(index: number) {
        // do not mutate, we need to create a new reference for React
        const animation = [...this.#animationConfigurations];
        animation.splice(index, 1);
        this.#animationConfigurations = animation;
        this.requiresUpdate = true;
    }

    override get propertiesToSerialize() {
        // why does getAttributesToSerialize(this) not work?
        return [
            'animationConfigurations',
            'enabled',
            'asMask',
            'level',
            'alignX',
            'alignY',
            'positionX',
            'positionY',
        ];
    }

    /** The list of properties that can be animated during draw */
    abstract get animatableProperties(): string[];

    /** The list of geometry properties that can be animated during draw. */
    abstract get animatableGeometricProperties(): string[];

    override get readonlyProperties() {
        return ['type'];
    }

    loadTextAssetStyle(data, settings: LoadSettings) {
        const newData = cloneDeep(data);
        if (data.alignGraphicX !== undefined) {
            newData.alignX = data.alignGraphicX;
        }
        if (data.alignGraphicY !== undefined) {
            newData.alignY = data.alignGraphicY;
        }
        newData.level = DynamicLevel[data.level];
        if (data.shape.positionX !== undefined) {
            newData.positionX = data.shape.positionX;
        }
        if (data.shape.positionY !== undefined) {
            newData.positionY = data.shape.positionY;
        }

        this.#animationConfigurations.splice(data.animation.length);
        newData.animation.forEach((data, index) => {
            const animation = this.#animationConfigurations[index] ?? this.addAnimation();
            animation.loadTextAssetStyle(data, settings);
        });

        // For object level, baseline used to be the same as bottom. For canvas it now actually uses the baseline (of
        // the first line) so we convert it to maintain previous behaviour. We probably want to remove baseline
        // from the options in this case (LU-3303).
        if (newData.level === DynamicLevel.object && newData.alignY === DynamicYAlign.baseline) {
            newData.alignY = DynamicYAlign.bottom;
        }

        this.load(newData, settings);
    }
}
